import React, { useState, useEffect } from 'react';
import { LogIn, User as UserIcon, Coins } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { LoginModal } from '@/components/auth/LoginModal';
import { SettingsModal } from '@/components/common/SettingsModal';
import { ResponsiveOverlay } from '@/components/ui/ResponsiveOverlay';
import { auth } from '@/services/firebase';
import toast from 'react-hot-toast';
import { useMutation } from '@tanstack/react-query';
import { AuthDropdown } from './AuthDropdown';
import { WorkspaceExportDialog } from '@/components/common/WorkspaceExportDialog';
import { WorkspaceImportDialog } from '@/components/common/WorkspaceImportDialog';
import { TopUpModal } from '@/components/common/TopUpModal';
import { TransactionsModal } from '@/components/common/TransactionsModal';
import { UsageHistoryModal } from '@/components/common/UsageHistoryModal';
import { AnimatePresence, motion } from 'motion/react';

export function AuthButton() {
  const { user, userData, loading, signOut } = useAuth();
  const [showLogin, setShowLogin] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [showExport, setShowExport] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [showTopUp, setShowTopUp] = useState(false);
  const [showTransactions, setShowTransactions] = useState(false);
  const [showUsages, setShowUsages] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const [isMobile, setIsMobile] = useState(typeof window !== 'undefined' ? window.innerWidth < 640 : false);

  useEffect(() => {
    let rafId: number | null = null;
    const checkIsMobile = () => {
      if (rafId) return;
      rafId = requestAnimationFrame(() => {
        setIsMobile(window.innerWidth < 640);
        rafId = null;
      });
    };
    checkIsMobile();
    window.addEventListener('resize', checkIsMobile);
    
    const handleOpenLogin = () => setShowLogin(true);
    document.addEventListener('open-login', handleOpenLogin);

    return () => {
      window.removeEventListener('resize', checkIsMobile);
      document.removeEventListener('open-login', handleOpenLogin);
    };
  }, []);

  const containerRef = React.useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent | TouchEvent) => {
      if (showMenu && containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setShowMenu(false);
      }
    };

    if (showMenu && !isMobile) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('touchstart', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [showMenu, isMobile]);

  if (loading) {
    return <div className="h-8 w-20 animate-pulse bg-slate-200 dark:bg-slate-800 rounded-md"></div>;
  }

  if (!user) {
    return (
      <>
        <button
          onClick={() => setShowLogin(true)}
          aria-label="Sign In"
          className="flex items-center gap-1.5 text-xs font-medium bg-emerald-600 hover:bg-emerald-700 text-white transition-colors h-8 px-3 rounded-md dark:shadow-sm cursor-pointer"
        >
          <LogIn className="w-3.5 h-3.5" />
          <span className="hidden sm:inline">Sign In</span>
        </button>
        <LoginModal isOpen={showLogin} onClose={() => setShowLogin(false)} />
      </>
    );
  }

  const freeTotal = userData ? userData.freeCredits || 0 : 0;
  const paidCredits = userData?.purchasedCredits || 0;
  const totalCredits = freeTotal + paidCredits;

  const getTextColor = (total: number) => {
    if (total < 10) return 'text-red-700 dark:text-red-300';
    return 'text-emerald-700 dark:text-emerald-300';
  };

  const getBgColor = (total: number) => {
    if (total < 10) return 'bg-red-100 dark:bg-red-900/40 border-[0.5px] border-red-200 dark:border-red-800/60';
    return 'bg-emerald-100 dark:bg-emerald-900/40 border-[0.5px] border-emerald-200 dark:border-emerald-800/60';
  };

   return (
     <div 
       ref={containerRef}
       className="flex items-center relative group"
       onMouseEnter={() => !isMobile && setShowMenu(true)}
       onMouseLeave={() => !isMobile && setShowMenu(false)}
     >
       <button 
         onClick={() => setShowMenu(prev => !prev)}
         className="flex items-center cursor-pointer outline-none text-left"
         aria-label="User Menu"
         aria-expanded={showMenu}
         aria-haspopup="true"
       >
         <div className="hidden sm:flex flex-col items-end pr-3 text-xs">
           {!user?.emailVerified ? (
             <span className="text-amber-500 font-medium whitespace-nowrap pt-1">Pending Verification</span>
           ) : (
             <>
               <div className={`flex items-center gap-1.5 px-2.5 py-1 rounded-md ${getBgColor(totalCredits)} transition-colors`}>
                 <div className={`flex items-center gap-1.5 ${getTextColor(totalCredits)} font-semibold whitespace-nowrap`} title="Remaining Credits">
                     <Coins className="w-3.5 h-3.5" />
                     <span className="text-[13px]">{totalCredits % 1 === 0 ? totalCredits : totalCredits.toFixed(1)}</span>
                 </div>
               </div>
             </>
           )}
         </div>
        
        <div className="flex items-center justify-center w-8 h-8 rounded-full bg-slate-200 dark:bg-slate-800 text-slate-700 dark:text-slate-300 hover:bg-slate-300 dark:hover:bg-slate-700 shrink-0 transition-colors">
          {user.photoURL ? (
            <img src={user.photoURL} loading="lazy" alt="Avatar" className="w-8 h-8 rounded-full object-cover pointer-events-none" />
          ) : (
            <UserIcon className="w-4 h-4 pointer-events-none" />
          )}
        </div>
       </button>

      <div 
        className="absolute right-0 top-full pt-1.5 z-50"
      >
        {isMobile ? (
          <ResponsiveOverlay 
            isOpen={showMenu} 
            onClose={() => setShowMenu(false)}
            variant="dropdown"
            className="w-full sm:w-56"
          >
            <AuthDropdown
              user={user}
              totalCredits={totalCredits}
              getTextColor={getTextColor}
              setShowTopUp={setShowTopUp}
              setShowMenu={setShowMenu}
              setShowSettings={setShowSettings}
              setShowExport={setShowExport}
              setShowImport={setShowImport}
              setShowTransactions={setShowTransactions}
              setShowUsages={setShowUsages}
              signOut={signOut}
            />
          </ResponsiveOverlay>
        ) : (
          <AnimatePresence>
            {showMenu && (
              <motion.div 
                initial={{ opacity: 0, y: -10, scale: 0.95 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: -10, scale: 0.95 }}
                transition={{ duration: 0.15, ease: "easeOut" }}
                className="w-56 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-md dark:shadow-xl overflow-hidden mt-1 transform origin-top-right relative z-50 text-left"
              >
                <AuthDropdown
                  user={user}
                  totalCredits={totalCredits}
                  getTextColor={getTextColor}
                  setShowTopUp={setShowTopUp}
                  setShowMenu={setShowMenu}
                  setShowSettings={setShowSettings}
                  setShowExport={setShowExport}
                  setShowImport={setShowImport}
                  setShowTransactions={setShowTransactions}
                  setShowUsages={setShowUsages}
                  signOut={signOut}
                />
              </motion.div>
            )}
          </AnimatePresence>
        )}
      </div>
      
      {showSettings && <SettingsModal isOpen={showSettings} onClose={() => setShowSettings(false)} />}
      <WorkspaceExportDialog externalOpen={showExport} setExternalOpen={setShowExport} className="hidden" />
      <WorkspaceImportDialog externalOpen={showImport} setExternalOpen={setShowImport} className="hidden" />
      <TopUpModal isOpen={showTopUp} onClose={() => setShowTopUp(false)} />
      <TransactionsModal isOpen={showTransactions} onClose={() => setShowTransactions(false)} />
      <UsageHistoryModal isOpen={showUsages} onClose={() => setShowUsages(false)} />
    </div>
  );
}
